import { Computer } from '@assets/icons/Computer';
import { Pin } from '@assets/icons/Pin';
import { FOOD_PREFERENCE } from '@common/types/user';
import { RadioCard } from '@components/display/cards/RadioCard';
import { CloseButton } from '@components/utility/buttons';
import { Checkbox } from '@components/utility/checkboxes';
import { Select } from '@components/utility/selects';
import { FailedStatus } from '@event/components/display/dialogs/RegistrationFailedDialog';
import { COLLEAGUE_HASH_KEY, GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { HybridEventSchema } from '@event/schemas/HybridEventSchema';
import {
    useAddNonParticipantMutation,
    useLazyGetPublicEventQuery,
    useRegisterUserMutation,
    useUnregisterUserMutation,
} from '@event/services/events';
import { EventTypes, IEvent, PublicOnSiteEvent, RegistrationCardStatus } from '@event/types/event';
import { useAuth } from '@hooks/useAuth';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import { useScrollIntoError } from '@hooks/useScrollIntoError';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useLazyGetUserByGuestHashQuery, usePatchFoodPreferenceMutation } from '@services/accounts';
import { handleError, toaster } from '@utils/common';
import { isApiResponseError } from '@utils/service';
import { FOOD_PREFERENCES } from '@utils/user';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

type RegistrationDetails = {
    email: string;
    first_name: string;
    last_name: string;
    company_name?: string;
};

const getOppositeEventType = (currentEventType: EventTypes) => {
    switch (currentEventType) {
        case EventTypes.PRIVATE_ONSITE:
            return EventTypes.PRIVATE_VIRTUAL;
        case EventTypes.PRIVATE_VIRTUAL:
            return EventTypes.PRIVATE_ONSITE;
        case EventTypes.PUBLIC_ONSITE:
            return EventTypes.PUBLIC_VIRTUAL;
        case EventTypes.PUBLIC_VIRTUAL:
            return EventTypes.PUBLIC_ONSITE;
    }
};

const HybridEventDialog = ({
    subscription,
    registrationDetails,
    setFailedStatus,
    ...props
}: DialogProps & {
    subscription?: {
        hybrid_event?: IEvent['hybrid_event'];
    };
    registrationDetails?: RegistrationDetails;
    setFailedStatus: React.Dispatch<React.SetStateAction<FailedStatus | undefined>>;
}) => {
    const { user } = useAuth();
    const {
        event: { data: event },
        guestUserInfo: { data: guestUserInfo },
        guestAccessHash,
    } = usePublicEventContext();
    const navigate = usePrefixedNavigate();
    const intl = useIntl();
    const foodPreferenceOptions = FOOD_PREFERENCES(intl);

    const { type: eType, eventId } = useParams();
    const eventType = eType as EventTypes;
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isDeclineLoading, setIsDeclineLoading] = useState(false);

    const [registerUser] = useRegisterUserMutation();
    const [unregisterUser] = useUnregisterUserMutation();
    const [addNonParticipant] = useAddNonParticipantMutation();
    const [getUserByGuestHash] = useLazyGetUserByGuestHashQuery();
    const [patchFoodPreference] = usePatchFoodPreferenceMutation();
    const [getEventSubscription] = useLazyGetPublicEventQuery();

    const getGuestAccessHash = (type: EventTypes) =>
        type.includes('event')
            ? subscription?.hybrid_event?.onsite_event.guest_access_hash
            : subscription?.hybrid_event?.online_event?.guest_access_hash;

    const formik = useFormik({
        initialValues: {
            first_name: registrationDetails?.first_name ?? user?.first_name,
            last_name: registrationDetails?.last_name ?? user?.last_name,
            company: registrationDetails?.company_name ?? user?.company_name,
            type: eventType.includes('webinar') ? 'online' : 'onsite',
            attend_dinner: eventType.includes('webinar')
                ? false
                : !!(event as PublicOnSiteEvent).subscriber_attend_dinner,
            food_preference: user?.food_preference ?? guestUserInfo?.food_preference ?? FOOD_PREFERENCE.NO_PREFERENCE,
        },
        validationSchema: HybridEventSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
                const selectedEventType =
                    type === 'onsite'
                        ? eventType.includes('private')
                            ? EventTypes.PRIVATE_ONSITE
                            : EventTypes.PUBLIC_ONSITE
                        : eventType.includes('private')
                        ? EventTypes.PRIVATE_VIRTUAL
                        : EventTypes.PUBLIC_VIRTUAL;
                const selectedEventId =
                    type === 'onsite'
                        ? Number(subscription?.hybrid_event?.onsite_event?.id)
                        : Number(subscription?.hybrid_event?.online_event?.id);
                const oppositeEventId =
                    type === 'onsite'
                        ? Number(subscription?.hybrid_event?.online_event?.id)
                        : Number(subscription?.hybrid_event?.onsite_event?.id);
                const oppositeEventType = getOppositeEventType(selectedEventType);
                const eventGuestAccessHash =
                    getGuestAccessHash(selectedEventType) ??
                    (selectedEventType === eventType ? guestAccessHash : undefined);

                let registeredGuestAccessHash = eventGuestAccessHash;

                if (user || eventGuestAccessHash) {
                    await registerUser({
                        id: selectedEventId,
                        type: selectedEventType,
                        attend_dinner: values.attend_dinner,
                        guestAccessHash: eventGuestAccessHash,
                    }).unwrap();
                } else {
                    const currentEventGuestAccessHash =
                        getGuestAccessHash(eventType) ?? guestAccessHash ?? getGuestAccessHash(oppositeEventType);
                    const userDetails =
                        registrationDetails ??
                        (currentEventGuestAccessHash === guestAccessHash ? guestUserInfo : undefined) ??
                        (currentEventGuestAccessHash
                            ? await getUserByGuestHash({
                                  guest_access_hash: currentEventGuestAccessHash,
                              }).unwrap()
                            : undefined);

                    if (!userDetails) {
                        throw new Error('Guest details are unavailable for hybrid event registration');
                    }

                    await addNonParticipant({
                        id: selectedEventId,
                        type: selectedEventType,
                        email: userDetails.email,
                        first_name: userDetails.first_name,
                        last_name: userDetails.last_name,
                        company_name: userDetails.company_name ?? '',
                        colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                        attend_dinner: values.attend_dinner,
                        commit: false,
                    }).unwrap();
                    const registration = await addNonParticipant({
                        id: selectedEventId,
                        type: selectedEventType,
                        email: userDetails.email,
                        first_name: userDetails.first_name,
                        last_name: userDetails.last_name,
                        company_name: userDetails.company_name ?? '',
                        colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                        attend_dinner: values.attend_dinner,
                    }).unwrap();
                    registeredGuestAccessHash = registration.guest_access_hash;
                }

                const oppositeGuestAccessHash =
                    getGuestAccessHash(oppositeEventType) ??
                    (oppositeEventType === eventType ? guestAccessHash : undefined);
                const oppositeEventSubscription = await getEventSubscription({
                    id: oppositeEventId,
                    type: oppositeEventType,
                    colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                    guestAccessHash: oppositeGuestAccessHash,
                }).unwrap();

                if (oppositeEventSubscription.registration_status === RegistrationCardStatus.REGISTERED) {
                    try {
                        await unregisterUser({
                            id: oppositeEventId,
                            type: oppositeEventType,
                            colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                            guestAccessHash: oppositeGuestAccessHash,
                        }).unwrap();
                    } catch (err) {
                        if ((err as { status: number }).status !== 404) {
                            throw err;
                        }
                    }
                }

                if ((user || eventGuestAccessHash) && type === 'onsite' && values.attend_dinner) {
                    await patchFoodPreference({
                        food_preference: values.food_preference,
                        guest_access_hash: eventGuestAccessHash,
                    });
                }
                toaster.success({
                    msg:
                        values.type === 'onsite'
                            ? intl.formatMessage(
                                  {
                                      id: 'event.hybrid.registered.onsite',
                                      defaultMessage: 'You are now registered to the On Site version of {name}',
                                  },
                                  {
                                      name: event?.name,
                                  }
                              )
                            : intl.formatMessage(
                                  {
                                      id: 'event.hybrid.registered.online',
                                      defaultMessage: 'You are now registered to the Online version of {name}',
                                  },
                                  {
                                      name: event?.name,
                                  }
                              ),
                });

                const nextSearchParams = new URLSearchParams(searchParams);
                if (registeredGuestAccessHash) {
                    nextSearchParams.set(GUEST_ACCESS_HASH_KEY, registeredGuestAccessHash);
                } else {
                    nextSearchParams.delete(GUEST_ACCESS_HASH_KEY);
                }
                const query = nextSearchParams.toString();
                if (eventType !== selectedEventType) {
                    navigate(`/events/${selectedEventType}/${selectedEventId}${query ? `?${query}` : ''}`);
                } else if (registeredGuestAccessHash) {
                    setSearchParams(nextSearchParams);
                }
                handleClose();
            } catch (err) {
                let failedStatus: FailedStatus | undefined;
                if (isApiResponseError(err)) {
                    if (Array.isArray(err.data)) {
                        if (err.data[0]?.includes('seats available')) {
                            failedStatus = FailedStatus.MAX_CAPACITY;
                        } else if (err.data[0]?.includes(FailedStatus.LOGIN)) {
                            failedStatus = FailedStatus.LOGIN;
                        }
                    } else if (err.data.email?.[0]) {
                        failedStatus = err.data.email[0] as FailedStatus;
                    }
                }

                if (failedStatus) {
                    setFailedStatus(failedStatus);
                    props.onClose?.({}, 'escapeKeyDown');
                    return;
                }

                handleError(
                    intl.formatMessage({ id: 'error.registeringEvent', defaultMessage: 'Error registering in event' }),
                    err
                );
            } finally {
                setIsLoading(false);
            }
        },
        validateOnMount: true,
        enableReinitialize: true,
    });

    const {
        values: { type, attend_dinner, food_preference },
        setFieldValue,
        handleChange,
        resetForm,
        handleSubmit,
    } = formik;

    useScrollIntoError({ formik });

    const handleDecline = async () => {
        setIsDeclineLoading(true);
        const currentGuestAccessHash = getGuestAccessHash(eventType) ?? guestAccessHash;
        if (user || guestUserInfo || currentGuestAccessHash) {
            try {
                await unregisterUser({
                    id: Number(eventId),
                    type: eventType,
                    colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                    guestAccessHash: currentGuestAccessHash,
                }).unwrap();
                const oppositeEventType = getOppositeEventType(eventType);
                const oppositeEventId = oppositeEventType.includes('onsite')
                    ? Number(subscription?.hybrid_event?.onsite_event?.id)
                    : Number(subscription?.hybrid_event?.online_event?.id);
                const oppositeGuestAccessHash = getGuestAccessHash(oppositeEventType);
                const oppositeEventSubscription = await getEventSubscription({
                    id: oppositeEventId,
                    type: oppositeEventType,
                    colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                    guestAccessHash: oppositeGuestAccessHash,
                }).unwrap();

                if (oppositeEventSubscription.registration_status === RegistrationCardStatus.REGISTERED) {
                    await unregisterUser({
                        id: oppositeEventId,
                        type: oppositeEventType,
                        colleagueHash: searchParams.get(COLLEAGUE_HASH_KEY),
                        guestAccessHash: oppositeGuestAccessHash,
                    }).unwrap();
                }
                toaster.success({
                    msg: intl.formatMessage({
                        id: 'event.hybrid.unregistered',
                        defaultMessage: 'You have been unregistered',
                    }),
                });
            } catch (err) {
                const error = err as { status?: number };
                if (error.status !== 404 && error.status !== 400) {
                    handleError(
                        intl.formatMessage({ id: 'error.unableToUnregister', defaultMessage: 'Unable to unregister' }),
                        err
                    );
                } else {
                    toaster.success({
                        msg: intl.formatMessage({
                            id: 'event.hybrid.unregistered',
                            defaultMessage: 'You have been unregistered',
                        }),
                    });
                }
            } finally {
                setIsDeclineLoading(false);
                props.onClose?.({}, 'escapeKeyDown');
                return;
            }
        }
        setIsDeclineLoading(false);
        handleClose();
    };

    const handleClose = () => {
        resetForm();
        props.onClose?.({}, 'escapeKeyDown');
    };

    return (
        <form onSubmit={handleSubmit}>
            <Dialog
                {...props}
                onClose={handleClose}
                disablePortal
                slotProps={{
                    paper: {
                        className: 'min-w-0 max-w-[800px] p-[60px_10px_10px] md:p-[60px_45px_43px] rounded-2xl',
                    },
                }}
            >
                <CloseButton onClick={handleClose} />
                <DialogTitle
                    textAlign="center"
                    className="p-0 pb-[16px]"
                    fontFamily="museo-sans"
                    fontSize="32px !important"
                    fontWeight={400}
                    letterSpacing={-0.8}
                    lineHeight="110%"
                >
                    {event?.name}
                </DialogTitle>
                <DialogContent>
                    <div className="mb-[81px]">
                        <Typography textAlign="center" fontSize={16} letterSpacing={0}>
                            <FormattedMessage
                                id="event.hybrid.title"
                                defaultMessage="For {name}, we kindly ask you to indicate your preferred mode of participation. You may choose to attend in person at the event venue or virtually through an online platform."
                                values={{ name: event?.name }}
                            />
                        </Typography>
                        <Typography textAlign="center" fontSize={16} letterSpacing={0} marginTop={3}>
                            <FormattedMessage
                                id="event.hybrid.note"
                                defaultMessage="Please note that this invitation is for 1 person only."
                            />
                        </Typography>
                    </div>
                    <Typography
                        fontFamily="museo-sans"
                        fontWeight={600}
                        fontSize={16}
                        lineHeight="26px"
                        letterSpacing="-2%"
                        marginBottom="20px"
                    >
                        <FormattedMessage
                            id="event.hybrid.chooseAttendance"
                            defaultMessage="Choose How You Wish To Attend The Event."
                        />
                    </Typography>
                    <div className="flex flex-col gap-5">
                        <RadioCard
                            checked={type === 'onsite'}
                            onClick={() => setFieldValue('type', 'onsite')}
                            IconElement={Pin}
                        >
                            <div>
                                <div className="max-w-[200px]">
                                    <Typography fontWeight={700} fontSize={20} lineHeight="150%" letterSpacing={0}>
                                        <FormattedMessage id="event.onsite" defaultMessage="Onsite Event" />
                                    </Typography>
                                    <Typography fontSize={15} letterSpacing={0} lineHeight="100%">
                                        <FormattedMessage
                                            id="event.hybrid.onsite.description"
                                            defaultMessage="I wish to attend the onsite version of this event."
                                        />
                                    </Typography>
                                </div>
                            </div>
                        </RadioCard>
                        {type === 'onsite' ? (
                            <>
                                <Checkbox
                                    checked={attend_dinner}
                                    onChange={handleChange}
                                    name="attend_dinner"
                                    color="primary"
                                    labelProps={{
                                        label: intl.formatMessage(
                                            {
                                                id: 'event.hybrid.dinner',
                                                defaultMessage: 'I will attend {name} dinner',
                                            },
                                            { name: event?.name }
                                        ),
                                    }}
                                    className="py-0"
                                />
                                <Select
                                    items={foodPreferenceOptions}
                                    label={intl.formatMessage({
                                        id: 'event.hybrid.foodPreference',
                                        defaultMessage: 'Food Preference',
                                    })}
                                    type="default"
                                    value={food_preference}
                                    name="food_preference"
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    fcSX={{ size: 'small' }}
                                />
                            </>
                        ) : null}
                        <RadioCard
                            checked={type === 'online'}
                            onClick={() => setFieldValue('type', 'online')}
                            IconElement={Computer}
                        >
                            <div className="max-w-[230px]">
                                <Typography fontWeight={700} fontSize={20} lineHeight="150%" letterSpacing={0}>
                                    <FormattedMessage id="event.virtual" defaultMessage="Virtual Event" />
                                </Typography>
                                <Typography fontSize={15} letterSpacing={0} lineHeight="100%">
                                    <FormattedMessage
                                        id="event.hybrid.virtual.description"
                                        defaultMessage="I wish to attend the virtual version of this event via a meeting link."
                                    />
                                </Typography>
                            </div>
                        </RadioCard>
                    </div>
                </DialogContent>
                <DialogActions className="flex-col sm:flex-row gap-2 md:gap-5">
                    <Button
                        disabled={isDeclineLoading}
                        startIcon={isDeclineLoading ? <CircularProgress size={20} /> : <CloseIcon />}
                        color="secondary-alt"
                        onClick={handleDecline}
                        fullWidth
                        className="rounded-full black-border"
                    >
                        <Typography fontSize={15} fontWeight={500} letterSpacing={0.46}>
                            <FormattedMessage id="event.unregister" defaultMessage="Unregister" />
                        </Typography>
                    </Button>
                    <Button
                        type="submit"
                        disabled={isLoading || isDeclineLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
                        fullWidth
                        className="rounded-full black-border"
                    >
                        <Typography fontSize={15} fontWeight={500} letterSpacing={0.46}>
                            <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
                        </Typography>
                    </Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export { HybridEventDialog };
